import { staffPermissionCatalog, type StaffPermissionKey } from './staff-permissions';

/**
 * Sentences shown when the server refuses an action (HTTP 403). The API sends them as `error.message`
 * and the web app rebuilds them from `error.details`, so both sides stay worded the same way.
 */

export const ASK_OWNER_TO_ENABLE_PERMISSION =
  'Ask the salon owner to turn it on in Settings › Staff › Permissions.';

/** For refusals that depend on several toggles, so no single one can be named. */
export const ASK_OWNER_TO_CHECK_PERMISSIONS =
  'Ask the salon owner to check your permissions in Settings › Staff › Permissions.';

/** Owner/admin-only actions (roles, pay, salon-wide setup). */
export const ADMIN_ONLY_REFUSAL_MESSAGE = 'Only the salon owner or an admin can do this.';

/** `error.details.reason` for agency team accounts that may look at, but not change, front-desk work. */
export const AGENCY_VIEW_ONLY_REASON = 'AGENCY_VIEW_ONLY';

export const AGENCY_VIEW_ONLY_REFUSAL_MESSAGE =
  "View only: agency team accounts can't change Calendar, Turns or checkout. The salon makes these changes.";

/** Client screens that need both "last names" and "contact details" switched on. */
export const FULL_CLIENT_DETAILS_REFUSAL_MESSAGE =
  `You don't have permission to see clients' full names and contact details. ${ASK_OWNER_TO_ENABLE_PERMISSION}`;

/** Client picker and quick-add from the calendar or checkout. */
export const BOOKING_OR_CHECKOUT_REFUSAL_MESSAGE =
  `You don't have permission to book appointments or use checkout. ${ASK_OWNER_TO_ENABLE_PERMISSION}`;

/** Grouped appointments where some members belong to team members this person can't see or book for. */
export const GROUP_APPOINTMENTS_REFUSAL_MESSAGE =
  `This group includes appointments for team members you can't see or change. ${ASK_OWNER_TO_CHECK_PERMISSIONS}`;

export const LIVE_CAMPAIGN_CHANGE_REFUSAL_MESSAGE =
  `This campaign is live, so changing it needs permission to send campaigns. ${ASK_OWNER_TO_ENABLE_PERMISSION}`;

export const QUEUED_AUTOMATION_CHANGE_REFUSAL_MESSAGE =
  `Texts from this automation are already queued, so changing it needs permission to send campaigns. ${ASK_OWNER_TO_ENABLE_PERMISSION}`;

/** Any other refusal without a more specific sentence. */
export const GENERIC_FORBIDDEN_MESSAGE = "You don't have access to do this. Ask the salon owner.";

export function isStaffPermissionKey(value: unknown): value is StaffPermissionKey {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(staffPermissionCatalog, value);
}

/**
 * "You don't have permission to view other people's calendar. Ask the salon owner…" — built from the
 * same label the owner sees in Settings › Staff › Permissions (every label reads "Can …").
 */
export function describeMissingStaffPermission(key: StaffPermissionKey): string {
  const action = staffPermissionCatalog[key].label.replace(/^Can\s+/, '');
  return `You don't have permission to ${action}. ${ASK_OWNER_TO_ENABLE_PERMISSION}`;
}
